import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CloudOff } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fromPrice, inr, media } from "@/lib/format";
import { useKiosk } from "./KioskContext";

const MOBILE_RE = /^[6-9]\d{9}$/;
const nextDays = (n) => Array.from({ length: n }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d; });
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="mb-2 block text-[15px] font-medium text-[#6E6E73]">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-[14px] text-[#D70015]" data-testid="form-error">{error}</span>}
  </label>
);

const Confirmation = ({ m, type, queued, t, showroom, lead }) => {
  const navigate = useNavigate();
  useEffect(() => { const h = setTimeout(() => navigate("/kiosk"), 45000); return () => clearTimeout(h); }, [navigate]);
  const url = `${window.location.origin}/m/${m.slug}`;
  const wa = showroom?.whatsapp ? `https://wa.me/91${showroom.whatsapp}?text=${encodeURIComponent(`Hi ${showroom.name}, I'm ${lead.name}. I just requested a ${type === "test_ride" ? "test ride" : "quote"} for the ${m.name}${lead.preferred_date ? ` on ${lead.preferred_date}` : ""} at your kiosk.`)}` : null;
  return (
    <motion.main initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mx-auto grid max-w-[1100px] items-center gap-10 px-6 py-10 lg:grid-cols-2 pk:!grid-cols-1 pk:!pt-16" data-testid="lead-confirmation">
      <div>
        <span className="grid h-16 w-16 place-items-center rounded-full bg-[#0B8577] text-white"><Check size={34} /></span>
        <h1 className="font-display mt-6 text-5xl lg:text-[64px] font-extrabold leading-none tracking-[-0.045em]">{t("thanks")}</h1>
        <p className="mt-4 text-xl text-[#6E6E73]">{type === "test_ride" ? t("rideConfirmed") : t("quoteConfirmed")}</p>
        {queued && <p className="mt-4 flex items-center gap-2 rounded-2xl bg-[#FFF5E5] p-4 text-[15px] text-[#8F4B00]" data-testid="lead-queued-note"><CloudOff size={18} /> {t("queued")}</p>}
        <button onClick={() => navigate("/kiosk")} className="btn-pill btn-ink mt-8" data-testid="confirmation-done-btn">{t("done")}</button>
      </div>
      <div className="tile flex flex-col items-center p-10 text-center">
        <p className="font-display text-2xl font-bold tracking-[-0.02em]">{t("takeHome")}</p>
        <div className="mt-6 rounded-[24px] bg-white p-5"><QRCodeSVG value={url} size={220} fgColor="#1D1D1F" data-testid="take-home-qr" /></div>
        <p className="mt-5 max-w-xs text-[15px] text-[#6E6E73]">{t("scanQr")}</p>
        <p className="mt-2 text-[15px] font-semibold">{m.name}</p>
        {wa && (
          <div className="mt-6 flex items-center gap-4 rounded-[24px] bg-white p-4 text-left" data-testid="whatsapp-block">
            <QRCodeSVG value={wa} size={96} fgColor="#1D1D1F" data-testid="whatsapp-qr" />
            <div><p className="font-semibold">{t("whatsapp")}</p><p className="text-[13px] text-[#6E6E73]">{t("whatsappScan")}</p></div>
          </div>
        )}
      </div>
    </motion.main>
  );
};

export default function LeadForm() {
  const { type } = useParams();
  const [params] = useSearchParams();
  const { data, t, submitLead, logEvent } = useKiosk();
  const [acc, setAcc] = useState([]);
  const [slug, setSlug] = useState(params.get("model") || data.models[0].slug);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [date, setDate] = useState(isoDay(new Date()));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const m = data.models.find((x) => x.slug === slug) || data.models[0];
  const days = useMemo(() => nextDays(7), []);
  const kind = type === "quote" ? "quote" : "test_ride";
  useEffect(() => { logEvent("book_start", slug); }, [slug, logEvent]);
  const accList = (data.accessories || []).filter((a) => a.applies_to === "all" || a.applies_to === (m.category === "Electric" ? "Scooter" : m.category));
  const accTotal = accList.filter((a) => acc.includes(a.id)).reduce((s, a) => s + a.price, 0);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (name.trim().length < 2) errs.name = t("invalidName");
    if (!MOBILE_RE.test(mobile)) errs.mobile = t("invalidMobile");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const r = await submitLead({ type: kind, name: name.trim(), mobile, model_slug: slug, preferred_date: date, accessories: kind === "quote" ? acc.filter((id) => accList.some((a) => a.id === id)) : [] });
    setBusy(false);
    if (r.status === "invalid") return setErrors({ mobile: r.detail });
    if (r.status === "limited") return setErrors({ mobile: t(r.detail === "too_many_mobile" ? "tooMany" : "tooBusy") });
    setDone(r);
  };

  if (done) return <Confirmation m={m} type={kind} queued={done.status === "queued"} t={t} showroom={data.showroom} lead={{ name, preferred_date: date }} />;
  const dayLabel = (d, i) => (i === 0 ? t("today") : i === 1 ? t("tomorrow") : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }));

  return (
    <main className="mx-auto grid max-w-[1280px] gap-10 px-6 pb-10 pt-4 lg:grid-cols-[1fr_1.1fr] lg:px-10 pk:!grid-cols-1 pk:!pt-6" data-testid="lead-form-page">
      <div className="tile hidden flex-col justify-between p-8 lg:flex pk:!flex pk:!flex-row pk:!items-center pk:!gap-6">
        <div>
          <p className="text-[17px] font-semibold text-[#087568]">{kind === "test_ride" ? t("bookFor") : t("quoteFor")}</p>
          <h1 className="font-display text-[56px] font-extrabold leading-none tracking-[-0.045em]">{m.short}</h1>
          <p className="mt-2 text-[15px]">{t("from")} <span className="font-semibold tnum">{inr(fromPrice(m))}</span> · {t("exShowroom")}</p>
          <p className="text-[13px] text-[#6E6E73]">{t("indicative")}</p>
        </div>
        <div className="relative h-[300px] pk:!h-[220px] pk:!flex-1"><div className="floor-glow absolute bottom-0 left-1/2 h-16 w-[70%] -translate-x-1/2" /><img src={media(m.hero_image)} alt={m.name} className="product-shadow relative h-full w-full object-contain" /></div>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-6" data-testid="lead-form" noValidate>
        <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-[-0.04em]">{kind === "test_ride" ? t("bookTestRide") : t("getQuote")}</h2>
        <Field label={t("name")} error={errors.name}>
          <input data-testid="lead-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="off"
            className="h-14 w-full rounded-2xl bg-[#F5F5F7] px-5 text-[18px] outline-none ring-[#0B8577] focus:ring-2" />
        </Field>
        <Field label={t("mobile")} error={errors.mobile}>
          <div className="flex h-14 items-center rounded-2xl bg-[#F5F5F7] px-5 ring-[#0B8577] focus-within:ring-2">
            <span className="mr-3 text-[18px] text-[#6E6E73]">+91</span>
            <input data-testid="lead-mobile-input" inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              className="h-full flex-1 bg-transparent text-[18px] tracking-wide outline-none tnum" autoComplete="off" />
            {MOBILE_RE.test(mobile) && <Check size={20} className="text-[#0B8577]" data-testid="mobile-valid-icon" />}
          </div>
        </Field>
        <Field label={t("model")}>
          <Select value={slug} onValueChange={setSlug}>
            <SelectTrigger className="h-14 rounded-2xl border-0 bg-[#F5F5F7] px-5 text-[18px]" data-testid="lead-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>{data.models.map((x) => <SelectItem key={x.slug} value={x.slug} className="min-h-[44px] text-[16px]" data-testid={`lead-model-${x.slug}`}>{x.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("preferredDate")}>
          <div className="flex flex-wrap gap-2" data-testid="lead-date-chips">
            {days.map((d, i) => (
              <button type="button" key={isoDay(d)} onClick={() => setDate(isoDay(d))} data-testid={`lead-date-${i}`}
                className={`min-h-[52px] rounded-full px-5 text-[15px] font-medium transition-colors ${date === isoDay(d) ? "bg-[#1D1D1F] text-white" : "bg-[#F5F5F7]"}`}>{dayLabel(d, i)}</button>
            ))}
          </div>
        </Field>
        {kind === "quote" && accList.length > 0 && (
          <Field label={`${t("addAccessories")}${accTotal ? ` · ${inr(accTotal)}` : ""}`}>
            <div className="flex flex-wrap gap-2" data-testid="lead-accessories">
              {accList.map((a) => {
                const on = acc.includes(a.id);
                return <button type="button" key={a.id} onClick={() => setAcc(on ? acc.filter((x) => x !== a.id) : [...acc, a.id])} data-testid={`lead-acc-${a.id}`}
                  className={`min-h-[52px] rounded-full px-5 text-[15px] font-medium ${on ? "bg-[#0B8577] text-white" : "bg-[#F5F5F7]"}`}>{a.name} · {inr(a.price)}</button>;
              })}
            </div>
          </Field>
        )}
        <p className="text-[13px] text-[#6E6E73]">{t("whyAsk")}</p>
        <div className="flex gap-3">
          <Link to={`/kiosk/model/${slug}`} className="btn-pill btn-ghost" data-testid="lead-back-btn">{t("back")}</Link>
          <button type="submit" disabled={busy} className="btn-pill btn-teal flex-1" data-testid="lead-submit-btn">{kind === "test_ride" ? t("submitRide") : t("submitQuote")}</button>
        </div>
      </form>
    </main>
  );
}
